import React, { useMemo } from 'react';
import moment from 'moment';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import { Avatar, Box, Tooltip } from '@mui/material';
import { AssessmentSurvey } from '../../interfaces/assessment.interface';
import User, { Badge, CompanyCertification } from '../../interfaces/users.interface';
import { makeStyles } from '@mui/styles';
import { useAppSelector } from '../../app/hooks';
import { Star } from '@mui/icons-material';
import { BADGE_LABEL_IMAGEE_MAP, BLUE_BADGE_IMAGE, BLUE_BADGE_LABEL, BLUE_NEXT_GEN_BADGE_IMAGE, CERTIFICATION_BADGE_LABEL, GOLD_BADGE_LABEL, badgeFileMap, downloadWithWhiteBackground } from '../../services/badge';
import { USER_TYPES } from '../../services/user';

const useStyles = makeStyles(() => ({
  badge: {
    width: '55px',
    height: '55px',
    padding: '5px',
    backgroundColor: 'white',
  },
}));

type GetBadgeProps = {

  blueBadgeLabel?: string,
  goldBadgeLabel?: string,
  badge?: string,
  assessorLevel?: number,
  assessmentSurvey?: AssessmentSurvey,
  certification?: CompanyCertification,
  assessor?: User
  company?: number,
  isActive?: boolean,
  shield?: Badge
};

export default function GetBadge(props: GetBadgeProps) {
  const {
    blueBadgeLabel,
    goldBadgeLabel,
    badge:  badgeProp,
    assessorLevel,
    certification,
    company,
    isActive,
    shield,
  } = props;
  const { user } = useAppSelector(state => state.user);
  const classes = useStyles();
  const badge = shield?.title || badgeProp;
  let icon = <Box className={classes.badge}></Box>;
  const isNewBlueBadge = useMemo(() => blueBadgeLabel?.includes('v5.3.1') && badge === BLUE_BADGE_LABEL, [blueBadgeLabel, badge]);

  const hasBlue = Boolean(blueBadgeLabel);
  const hasGold = Boolean(goldBadgeLabel);
  const blueExpired = !!hasBlue && (blueBadgeLabel ?? '').toLowerCase().includes('expired');
  const goldExpired = !!hasGold && (goldBadgeLabel ?? '').toLowerCase().includes('expired');

  const showExpiredBanner =
    (hasBlue && !hasGold && blueExpired) ||
    (hasBlue && hasGold && blueExpired && goldExpired);

  if (assessorLevel) {
    icon = (
      <Tooltip title={`Assessor Level ${assessorLevel}`}>
        <Box sx={{ position: 'relative' }}>
          <Star color="primary" fontSize='large' sx={{ position: 'absolute', left: '50%', top: '50%', transform: 'translate(-50%, -50%)' }} />
          <Box sx={{ position: 'absolute', left: '50%', top: '50%', transform: 'translate(-50%, -50%)', color: 'white', fontSize: '.8em' }}>{assessorLevel}</Box>
        </Box>
      </Tooltip>
    );
  }
  if (!assessorLevel && badge == CERTIFICATION_BADGE_LABEL) {
    if (certification?.document) {
      icon = <a href={certification ? certification.document : '#'} target="_blank" rel="noreferrer"><Tooltip title={certification ? `${certification.certification.title} issued on ${moment(certification.issueDate).format('MM/DD/YYYY')}` : 'Certification Uploaded'}><ReceiptLongIcon fontSize='large' /></Tooltip></a>;
    } else {
      icon = <Tooltip title={(user?.type === USER_TYPES.TPN_ADMIN || user?.type === USER_TYPES.CONTENT_OWNER) ? (`Certification Uploaded${shield ? `: Expires ${moment(shield?.expirationDate).format('MM/DD/YYYY')}` : ''}`) : ''}><ReceiptLongIcon fontSize='large' /></Tooltip>;
    }
  }

  if (!assessorLevel && (blueBadgeLabel || goldBadgeLabel)) {
    const title = (
      <span>
        {blueBadgeLabel}
        {goldBadgeLabel && (
          <>
            {blueBadgeLabel  && <br /> }
            {goldBadgeLabel}
          </>
        )}
      </span>
    );

    const resolvedBadge = badge || '';
    const imgBodySrc = isNewBlueBadge ?
      BLUE_NEXT_GEN_BADGE_IMAGE :
      BADGE_LABEL_IMAGEE_MAP[resolvedBadge];
    const imgSrc = imgBodySrc || BLUE_BADGE_IMAGE;

    icon = (
      <Tooltip title={title}>
        <Box component='img' src={imgSrc} className={classes.badge} />
      </Tooltip>
    );
  }

  const hasBadgeOrLevel = Boolean(
    assessorLevel ||
    shield?.title ||
    badgeProp ||
    blueBadgeLabel ||
    goldBadgeLabel,
  );
  
  const getAvatar = (imgSrc: string, fileName: string) => (
    <Avatar
      onClick={(e) => {
        e.stopPropagation();
        if (goldBadgeLabel || blueBadgeLabel || badge) {
          downloadWithWhiteBackground(imgSrc, fileName);
        }
      }}
      sx={{
        backgroundColor: 'white',
        position: 'relative',
        overflow: 'hidden',
        minWidth: '63px',
        minHeight: '63px',
        width: 'fit-content',
        height: 'fit-content',
        justifyContent: 'center',
        alignItems: 'center',
        display: 'flex',
        alignContent: 'center',
        border: hasBadgeOrLevel ? '1px solid rgba(0, 0, 0, 0.05)' : 'none',
        boxShadow: hasBadgeOrLevel ? '0 2px 6px rgba(0, 0, 0, 0.10)' : 'none',
      }}
    >
      {icon}
      {showExpiredBanner && (
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          m: 'auto',
          borderRadius: '50%',
          bgcolor: '#fff',
          zIndex: 2,
          pointerEvents: 'none',
        }}
      />
      )}
    </Avatar>
  );

  const getBadgeImageName = () => {
    if (goldBadgeLabel || badge === GOLD_BADGE_LABEL) return 'Gold';
    if (isNewBlueBadge) return 'NextGen_Blue';
    return 'Blue';
  };
  
  const getBadgeFileName = () => {
    if (goldBadgeLabel || badge === GOLD_BADGE_LABEL) return 'Gold';
    if (isNewBlueBadge) return 'NextGen_Blue';
    return 'Blue';
  };

  const imgSrc = `${process.env.PUBLIC_URL}/assets/TPN_${getBadgeImageName()}LogoTransparent_1280.png`;
  const fileName = `TPN_${getBadgeFileName()}_Badge.png`;

  if (!company || !isActive || !(user?.companies?.length && user.companies[0].id === company)) {
    return getAvatar(imgSrc, fileName);
  }

  if (!assessorLevel && badge) {
    if (isNewBlueBadge) {
      return getAvatar(BLUE_BADGE_IMAGE, 'TPN_NextGen_Blue_Badge.png');
    } else {
      const badgeFileObject = badgeFileMap[badge];
      if (badgeFileObject) {
        return getAvatar(badgeFileObject.image, badgeFileObject.filename);
      }
    }
  }

  return getAvatar(imgSrc, fileName);
}